/**
 * utils.js
 * Utility functions for formatting, DOM manipulation, and helpers
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { icons } from './icons.js?v=302';

/**
 * Format inline markdown-style text (bold/italic)
 * Escapes HTML first, then applies simple formatting
 * @param {string} raw - Raw text input
 * @returns {string} HTML-safe formatted string
 */
export function formatInline(raw) {
  // Escape HTML first to avoid breaking the page or allowing HTML injection
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Then apply simple formatting tokens:
  // **bold**  -> <strong>bold</strong>
  // *italic*  -> <em>italic</em>
  return escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

/**
 * Escape HTML special characters
 * @param {string} text - Text to escape
 * @returns {string} HTML-safe string
 */
export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Format timestamp to human-readable time
 * Shows time for today, date + time for other days
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Formatted time string
 */
export function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Relative time description
 */
export function getRelativeTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  return date.toLocaleDateString();
}

/**
 * Format file size in human-readable units
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

/**
 * Update send button state based on input content and generation status
 * Swaps between send and cancel button during generation
 */
export function updateSendButton() {
  // Guard against null elements (can happen during initialization)
  if (!elements.input || !elements.sendBtn) return;

  const hasContent =
    elements.input.value.trim() || state.attachedFiles.length > 0;

  if (state.isGenerating) {
    // Show cancel button during generation with visual feedback
    elements.sendBtn.disabled = false;
    elements.sendBtn.classList.add("cancel-mode");
    elements.sendBtn.classList.add("cancelling"); // Add pulsing class
    elements.sendBtn.classList.remove("loading");
    elements.sendBtn.innerHTML = icons.stop;
    elements.sendBtn.setAttribute("aria-label", "Stop generating");
    elements.sendBtn.title = "Stop generating";
  } else {
    // Show send button when not generating
    elements.sendBtn.disabled = !hasContent;
    elements.sendBtn.classList.remove("cancel-mode");
    elements.sendBtn.classList.remove("cancelling");
    elements.sendBtn.classList.remove("loading");
    elements.sendBtn.innerHTML = icons.send;
    elements.sendBtn.setAttribute("aria-label", "Send");
    elements.sendBtn.title = "Send message";
  }
}

/**
 * Handle Enter key in input field (send message on Enter, allow Shift+Enter for newline)
 * @param {KeyboardEvent} e - Keyboard event
 */
export function handleInputKeydown(e) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    // Import sendMessage dynamically to avoid circular dependencies
    import('./chat.js?v=302').then(({ sendMessage }) => {
      sendMessage().catch((err) => {
        console.error("Failed to send message:", err);
        import('./ui.js?v=302').then(({ showToast }) => {
          showToast("Failed to send message", "error");
        });
      });
    });
  }
}

/**
 * Auto-resize textarea input based on content
 * Limits height to 200px and adds scroll indicator if needed
 */
export function autoResizeInput() {
  elements.input.style.height = "auto";
  const newHeight = Math.min(elements.input.scrollHeight, 200);
  elements.input.style.height = newHeight + "px";
  updateSendButton();

  // Show/hide scroll indicator
  if (elements.input.scrollHeight > 200) {
    elements.input.classList.add('has-scroll');
  } else {
    elements.input.classList.remove('has-scroll');
  }
}
